import { postStat } from 'api/stats/stats.api'
import React, { ReactElement, useEffect } from 'react'
import { clearChartState } from 'store/chart/chart'
import { dynamicClassName } from 'styles/helper'
import { getTabFromStorage } from './getTabFromStorage'
import styles from './Tabs.module.less'

type TabTitle = string

type TabListProps = {
  items: TabTitle[]
  selectedTab: number
  onClick: (index: number) => void
  innerLeftPadding?: boolean
  marginBottom?: boolean
  onBeforeChange?: (index: number) => boolean
}

export function TabList(props: TabListProps): ReactElement {
  const { items, selectedTab, onClick, innerLeftPadding, marginBottom = true, onBeforeChange } = props

  return (
    <div
      className={dynamicClassName({
        [styles.Tabs]: true,
        [styles.Tabs__marginBottom]: marginBottom,
      })}
    >
      <div className={styles.Tabs_BorderBottom} />

      {items.map((item, index) => {
        const isTabActive = selectedTab === index

        return (
          <div
            key={`tab_${index}`}
            className={dynamicClassName({
              [styles.Tabs_Tab]: true,
              [styles.Tabs_Tab__active]: isTabActive,
              [styles.Tabs_Tab__paddingLeft]: index === 0 && !!innerLeftPadding,
            })}
            onClick={() => {
              if (isTabActive) {
                return
              }
              if (onBeforeChange && !onBeforeChange(index)) {
                return
              }
              postStat('tab', `navigate-to-tab-${item ?? index}`)
              onClick(index)
            }}
          >
            <div
              className={dynamicClassName({
                [styles.Tabs_Tab_Title]: true,
                [styles.Tabs_Tab_Title__active]: isTabActive,
              })}
            >
              {item}
              {index === selectedTab && <div className={styles.Tabs_Tab_BorderBottom} />}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export type TabItem = {
  title: TabTitle
  children: ReactElement[] | ReactElement
  id?: string
}

type TabsProps = {
  items: TabItem[]
  innerLeftPadding?: boolean
  marginBottom?: boolean
  uniqueStorageKey: string
  onChange?: (index: number, id?: string) => void
  onBeforeChange?: (index: number, id?: string) => boolean
}

export default function Tabs({
  items,
  innerLeftPadding,
  marginBottom = true,
  onChange,
  onBeforeChange,
  uniqueStorageKey,
}: TabsProps): ReactElement {
  const localStorageKey = `activeTab_${uniqueStorageKey}`
  const savedTabIndex = getTabFromStorage(localStorageKey, 0)
  const initialTab = savedTabIndex < items.length ? savedTabIndex : 0

  const [activeTabIndex, setActiveTabIndex] = React.useState(initialTab)
  const activeChildren = items[activeTabIndex]?.children

  useEffect(() => {
    localStorage.setItem(localStorageKey, activeTabIndex.toString())
    onChange?.(activeTabIndex, items[activeTabIndex]?.id)
  }, [activeTabIndex, localStorageKey, onChange, items])

  function onTabChange(index: number): void {
    if (onChange) {
      onChange(index, items[index]?.id)
    }

    // Changing tabs should have the same effect as changing module or reloading content. But Charts won't completely
    // update on state alone, we need to trigger the clear chart state as well.
    // See: https://app-eu.wrike.com/open.htm?id=1266563243
    clearChartState()
  }

  return (
    <>
      <TabList
        items={items.map((item) => item.title)}
        selectedTab={activeTabIndex}
        onClick={(index) => {
          setActiveTabIndex(index)
          onTabChange(index)
        }}
        innerLeftPadding={innerLeftPadding}
        marginBottom={marginBottom}
        onBeforeChange={(index) => onBeforeChange?.(index, items[index]?.id) ?? true}
      />
      {activeChildren && (
        <div className={styles.Tabs_Body} key={activeTabIndex}>
          {activeChildren}
        </div>
      )}
    </>
  )
}
